import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useDropzone } from "react-dropzone";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { accountCurrency, brokerDefaultCurrency } from "../lib/currency";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CheckCircle, Warning } from "@phosphor-icons/react";

const SAMPLE_ROWS = {
  generic: "symbol,direction,quantity,entry_price,exit_price,entry_time,exit_time,fees,notes\nRELIANCE,long,10,2400,2450,2026-01-15T09:30:00Z,2026-01-15T15:00:00Z,50,Breakout",
  zerodha: "Symbol,Trade Type,Quantity,Buy Average,Sell Average,Trade Date,Brokerage,Remarks\nRELIANCE,BUY,10,2400,2450,2026-01-15,50,",
};

const SCHEMA_FIELDS = ["symbol", "direction", "quantity", "entry_price", "exit_price", "entry_time", "exit_time", "fees", "notes"];

export default function CSVImportDialog({ open, onOpenChange, onImported }) {
  const { accounts, activeId, setActive, reload } = useAccounts();
  const [selectedAccount, setSelectedAccount] = useState(activeId);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [broker, setBroker] = useState("auto");
  const [step, setStep] = useState("upload"); // upload | preview
  const [preview, setPreview] = useState(null);
  const [editMap, setEditMap] = useState({});

  useEffect(() => { api.get("/brokers").then(({ data }) => setBrokers(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!open) { setStep("upload"); setPreview(null); setFile(null); }
    else if (activeId && !selectedAccount) { setSelectedAccount(activeId); }
  }, [open, activeId, selectedAccount]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] },
    maxFiles: 1,
    onDrop: (files) => setFile(files[0]),
  });

  const downloadSample = () => {
    const key = SAMPLE_ROWS[broker] ? broker : "generic";
    const csv = SAMPLE_ROWS[key];
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `tradelog-${key}-sample.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const brokerCcy = useMemo(() => brokerDefaultCurrency(broker), [broker]);
  const targetAcc = useMemo(() => accounts.find(a => a.id === selectedAccount) || null, [accounts, selectedAccount]);
  const targetAccCcy = useMemo(() => accountCurrency(targetAcc), [targetAcc]);
  const currencyMismatch = broker !== "auto" && brokerCcy && brokerCcy !== targetAccCcy;

  const createMatchingAccount = async () => {
    // Use just the exchange name (broker id title-cased) for a clean sidebar label —
    // e.g. "Exness" or "Zerodha", not "Exness (Terminal + Web/Mobile) (USD)".
    const shortName = broker.charAt(0).toUpperCase() + broker.slice(1);
    try {
      const { data } = await api.post("/accounts", {
        name: shortName,
        broker,
        starting_balance: 0,
        currency: brokerCcy,
      });
      await reload();
      setSelectedAccount(data.id);
      setActive?.(data.id);
      toast.success(`Created "${data.name}" (${brokerCcy})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create account");
    }
  };

  const doPreview = async () => {
    if (!file) return toast.error("Pick a CSV");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/trades/csv/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setEditMap(data.mapping || {});
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const doImport = async (useMapping) => {
    if (!file) return toast.error("Pick a CSV");
    const acc = selectedAccount || activeId;
    if (!acc) return toast.error("No account selected");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (useMapping) fd.append("mapping", JSON.stringify(editMap));
      const { data } = await api.post(
        `/trades/import-csv?account_id=${acc}&broker=${broker}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported ${data.inserted} trades`, {
        description: [
          data.used_manual_mapping && "Used your approved mapping",
          data.used_learned_template && "Reused your learned template",
          data.used_community_template && "Reused community template",
          data.ai_hint === "Auto-mapped columns" && !data.used_manual_mapping && "Auto-mapped for unknown broker",
          data.skipped && `${data.skipped} skipped`,
          data.errors && `${data.errors} errors`,
        ].filter(Boolean).join(" · "),
      });
      // Point the sidebar to the account we just imported into
      if (acc && acc !== activeId) setActive?.(acc);
      onImported?.(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  const isAutoDetect = broker === "auto";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto text-foreground">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">
            {step === "preview" ? "Review column mapping" : "Import CSV"}
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <>
            <div>
              <Label>Broker format</Label>
              <Select value={broker} onValueChange={setBroker}>
                <SelectTrigger data-testid="broker-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect (recommended)</SelectItem>
                  {brokers.map(b => {
                    const ccy = brokerDefaultCurrency(b.id);
                    return (
                      <SelectItem key={b.id} value={b.id} data-testid={`broker-${b.id}`}>
                        {b.name}{ccy ? ` · ${ccy}` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {isAutoDetect ? "We'll detect known brokers and, for unknown formats, let you review and edit the column mapping before importing." :
                 (brokers.find(b => b.id === broker)?.hint || "")}
              </p>
            </div>

            <div>
              <Label>Import into account</Label>
              <Select value={selectedAccount || ""} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="import-account-select">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id} data-testid={`import-acc-${a.id}`}>
                      {a.name} · {accountCurrency(a)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currencyMismatch && (
                <div className="mt-2 rounded-md border border-amber-500/50 bg-amber-500/10 dark:bg-amber-500/[0.06] p-3 text-xs flex items-start gap-2"
                  data-testid="ccy-mismatch-warn">
                  <Warning size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-foreground">
                      <b>{brokers.find(b => b.id === broker)?.name || broker}</b> trades are in <b>{brokerCcy}</b>,
                      but <b>{targetAcc?.name}</b> is in <b>{targetAccCcy}</b>. Your P&L would render with the wrong symbol.
                    </p>
                    <button
                      onClick={createMatchingAccount}
                      className="mt-1.5 inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200"
                      data-testid="create-matching-acc-btn">
                      + Create a new {brokerCcy} account for this import
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div {...getRootProps()} data-testid="csv-dropzone"
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
              <input {...getInputProps()} />
              <UploadSimple size={28} className="mx-auto text-muted-foreground mb-2" />
              {file ? <p className="text-sm">{file.name}</p> : <p className="text-sm">Drop CSV here or click to select</p>}
            </div>
            <button onClick={downloadSample} className="text-xs text-primary underline flex items-center gap-1 mt-1" data-testid="download-sample-btn">
              <DownloadSimple size={12} /> Download sample CSV
            </button>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              {isAutoDetect ? (
                <Button onClick={doPreview} disabled={loading || !file} className="rounded-full" data-testid="csv-preview-btn">
                  {loading ? "Analyzing…" : "Preview mapping"}
                </Button>
              ) : (
                <Button onClick={() => doImport(false)} disabled={loading || !file} className="rounded-full" data-testid="csv-import-btn">
                  {loading ? "Importing…" : "Import"}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {step === "preview" && preview && (
          <>
            <div className={`text-xs rounded-md border p-3 flex items-start gap-2 ${preview.source === "ai" ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/40"}`}>
              {preview.source === "ai" && <CheckCircle size={14} className="text-primary shrink-0 mt-0.5" />}
              <div data-testid="mapping-source-hint">
                {preview.source === "ai" ? (
                  <>
                    <p className="font-medium text-primary">Auto-mapped columns — please review</p>
                    <p className="text-muted-foreground mt-0.5">We couldn't recognise this broker format, so we mapped it automatically. Confirm the columns below before importing. {preview.row_count} rows detected.</p>
                  </>
                ) : preview.source && preview.source.startsWith("preset:") ? (
                  <p>Detected broker: <span className="font-medium">{preview.source.replace("preset:", "")}</span> · {preview.row_count} rows in file. Review and approve to import.</p>
                ) : (
                  <p>{preview.row_count} rows in file — review the mapping below and approve to import.</p>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Our field</th>
                    <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">CSV column</th>
                  </tr>
                </thead>
                <tbody>
                  {SCHEMA_FIELDS.map(f => (
                    <tr key={f} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{f}</td>
                      <td className="px-3 py-2">
                        <Select value={editMap[f] || "__none__"} onValueChange={v => setEditMap({ ...editMap, [f]: v === "__none__" ? null : v })}>
                          <SelectTrigger data-testid={`map-${f}`} className="h-8 text-xs bg-transparent"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— (skip)</SelectItem>
                            {preview.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground">Sample row: <span className="font-mono">{JSON.stringify(preview.sample_rows[0] || {}).slice(0, 200)}</span></p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("upload")}>Back</Button>
              <Button onClick={() => doImport(true)} disabled={loading} className="rounded-full" data-testid="csv-confirm-import-btn">
                <CheckCircle size={14} className="mr-1" />{loading ? "Importing…" : "Approve & import"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
